import { createAsyncThunk, createSelector, createSlice, type ThunkAction, type ThunkDispatch, type UnknownAction } from '@reduxjs/toolkit';
import {
  DraftConflictError,
  DraftRuleError,
  recipeDraftApi,
  type DraftView,
  type Finding,
  type StageRef,
  type StepName,
} from '../../services/recipeDraftApi';
import { stepAccess, type StepAccess } from '../../utils/stepAccess';

/**
 * A cache of the server's recipe drafts, one per season stage. The server decides everything; this slice only
 * remembers the last draft it was given, so screens can render it and send the right `rev` back.
 */

export interface DraftEntry {
  draft: DraftView | null;
  status: 'idle' | 'loading' | 'saving' | 'error';
  error: string | null;
  /** The last write was refused because the draft changed elsewhere; `draft` is already the current one. */
  conflict: boolean;
}

export interface RecipeDraftState {
  byStage: Record<string, DraftEntry>;
}

type Rejection =
  | { kind: 'conflict'; draft: DraftView; message: string }
  | { kind: 'rule'; draft: DraftView; message: string }
  | { kind: 'error'; message: string };

type StateWithDraft = { recipeDraft: RecipeDraftState };

const initialState: RecipeDraftState = { byStage: {} };
const blank = (): DraftEntry => ({ draft: null, status: 'idle', error: null, conflict: false });

function toRejection(err: unknown): Rejection {
  if (err instanceof DraftConflictError) return { kind: 'conflict', draft: err.draft, message: err.message };
  if (err instanceof DraftRuleError) return { kind: 'rule', draft: err.draft, message: err.message };
  const e = err as { response?: { data?: { error?: string } }; message?: string } | null;
  return { kind: 'error', message: e?.response?.data?.error || e?.message || 'Something went wrong. Try again.' };
}

const stageIdOf = (arg: { ref: StageRef }) => arg.ref.stageId;

/** Runs a write against the stored rev. Without a loaded draft there is no rev to send, so it refuses. */
function writeThunk<Arg extends { ref: StageRef }>(
  type: string,
  call: (arg: Arg, rev: number) => Promise<DraftView>,
) {
  return createAsyncThunk<DraftView, Arg, { state: StateWithDraft; rejectValue: Rejection }>(
    type,
    async (arg, { getState, rejectWithValue }) => {
      const rev = getState().recipeDraft.byStage[stageIdOf(arg)]?.draft?.rev;
      if (rev === undefined) return rejectWithValue({ kind: 'error', message: 'Load the draft first' });
      try {
        return await call(arg, rev);
      } catch (err) {
        return rejectWithValue(toRejection(err));
      }
    },
  );
}

// Several screens on one page ask for the same draft in the same moment; one request is enough for all of them.
export const loadDraft = createAsyncThunk<DraftView, { ref: StageRef; force?: boolean }, { state: StateWithDraft; rejectValue: Rejection }>(
  'recipeDraft/load',
  async ({ ref }, { rejectWithValue }) => {
    try {
      return await recipeDraftApi.get(ref);
    } catch (err) {
      return rejectWithValue(toRejection(err));
    }
  },
  // `force` is for a reload that must not be skipped (after issuing, the draft has just changed).
  { condition: ({ ref, force }, { getState }) => force === true || getState().recipeDraft.byStage[ref.stageId]?.status !== 'loading' },
);

// Writes of one stage run one at a time. Each reads the rev only when its turn comes, after the write before it
// has been stored, so back-to-back writes (a save then a check, or a debounced recalculation) send consecutive revs
// instead of the same one and never collide with themselves.
const queues = new Map<string, Promise<unknown>>();
function enqueue<T>(stageId: string, task: () => Promise<T>): Promise<T> {
  const turn = (queues.get(stageId) ?? Promise.resolve()).then(task, task);
  queues.set(stageId, turn.then(() => undefined, () => undefined));
  return turn;
}

function queued<Arg extends { ref: StageRef }>(
  base: (arg: Arg) => ThunkAction<Promise<unknown>, StateWithDraft, unknown, UnknownAction>,
) {
  return (arg: Arg) => (dispatch: ThunkDispatch<StateWithDraft, unknown, UnknownAction>) =>
    enqueue(stageIdOf(arg), () => dispatch(base(arg)));
}

const saveInputsBase = writeThunk<{ ref: StageRef; step: StepName; inputs: unknown }>(
  'recipeDraft/saveInputs',
  ({ ref, step, inputs }, rev) => recipeDraftApi.putInputs(ref, step, rev, inputs),
);

const runCheckBase = writeThunk<{ ref: StageRef; step: StepName; clientFindings?: Finding[] }>(
  'recipeDraft/runCheck',
  ({ ref, step, clientFindings }, rev) => recipeDraftApi.check(ref, step, rev, clientFindings),
);

const confirmStepBase = writeThunk<{ ref: StageRef; step: StepName; inputsHash: string; acknowledgeWarnings?: boolean }>(
  'recipeDraft/confirmStep',
  ({ ref, step, inputsHash, acknowledgeWarnings }, rev) => recipeDraftApi.confirm(ref, step, rev, inputsHash, acknowledgeWarnings),
);

const setStageOverrideBase = writeThunk<{ ref: StageRef; overridden: boolean }>(
  'recipeDraft/setStageOverride',
  ({ ref, overridden }, rev) => recipeDraftApi.setStageOverride(ref, rev, overridden),
);

export const saveInputs = queued(saveInputsBase);
export const runCheck = queued(runCheckBase);
export const confirmStep = queued(confirmStepBase);
export const setStageOverride = queued(setStageOverrideBase);

export type IssueResult = { ok: true; runId: string } | { ok: false; message: string };

/** Issues the run for a confirmed draft. Queued with the other writes; the draft is reloaded afterwards because RESULTS changes. */
export const issueRun = (arg: { ref: StageRef }) =>
  (dispatch: ThunkDispatch<StateWithDraft, unknown, UnknownAction>, getState: () => StateWithDraft): Promise<IssueResult> =>
    enqueue(stageIdOf(arg), async (): Promise<IssueResult> => {
      const rev = getState().recipeDraft.byStage[stageIdOf(arg)]?.draft?.rev;
      if (rev === undefined) return { ok: false, message: 'Load the draft first' };
      try {
        const { runId } = await recipeDraftApi.issue(arg.ref, rev);
        await dispatch(loadDraft({ ref: arg.ref, force: true }));
        return { ok: true, runId };
      } catch (err) {
        const why = toRejection(err);
        if (why.kind === 'error') return { ok: false, message: why.message };
        await dispatch(loadDraft({ ref: arg.ref, force: true }));
        return { ok: false, message: why.message };
      }
    });

const thunks = [loadDraft, saveInputsBase, runCheckBase, confirmStepBase, setStageOverrideBase];

/** Take the incoming draft unless the cache already holds a newer one (replies can arrive out of order). */
function adopt(entry: DraftEntry, incoming: DraftView): boolean {
  if (entry.draft && incoming.rev < entry.draft.rev) return false;
  const advanced = !entry.draft || incoming.rev > entry.draft.rev;
  entry.draft = incoming;
  return advanced;
}

const slice = createSlice({
  name: 'recipeDraft',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    for (const thunk of thunks) {
      const isLoad = thunk === loadDraft;
      builder
        .addCase(thunk.pending, (state, action) => {
          const entry = (state.byStage[stageIdOf(action.meta.arg as { ref: StageRef })] ??= blank());
          entry.status = isLoad ? 'loading' : 'saving';
          entry.error = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          const entry = (state.byStage[stageIdOf(action.meta.arg as { ref: StageRef })] ??= blank());
          adopt(entry, action.payload as DraftView);
          entry.status = 'idle';
          entry.error = null;
          entry.conflict = false;
        })
        .addCase(thunk.rejected, (state, action) => {
          const entry = (state.byStage[stageIdOf(action.meta.arg as { ref: StageRef })] ??= blank());
          const why = (action.payload as Rejection | undefined) ?? { kind: 'error', message: action.error.message || 'Something went wrong.' };
          entry.error = why.message;
          if (why.kind === 'error') {
            entry.status = 'error';
            return;
          }
          const advanced = adopt(entry, why.draft);
          entry.status = 'idle';
          entry.conflict = why.kind === 'conflict' && advanced;
        });
    }
  },
});

export const selectDraft = (state: StateWithDraft, stageId: string): DraftView | null =>
  state.recipeDraft.byStage[stageId]?.draft ?? null;

// Memoised on the draft object, so a component gets the same access object until the draft really changes.
export const selectStepAccess: (state: StateWithDraft, stageId: string) => Record<StepName, StepAccess> =
  createSelector([selectDraft], (draft) => stepAccess(draft));

export default slice.reducer;
